import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import ServicesSection from './components/ServicesSection';
import StrengthsSection from './components/StrengthsSection';
import PricingSection from './components/PricingSection';
import FlowSection from './components/FlowSection';
import FaqSection from './components/FaqSection';
import ContactSection from './components/ContactSection';
import CrossLinkBanner from './components/CrossLinkBanner';
import CaseStudySection from '../home/components/CaseStudySection';
import TaxOfficeSection from './components/TaxOfficeSection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function ManagementPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const title = t('seo.management.title');
    const description = t('seo.management.description');

    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="keywords"]', 'content', t('seo.management.keywords'));
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', `${SITE_URL}/management`);
    setMeta('link[rel="canonical"]', 'href', `${SITE_URL}/management`);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);

    const schemaId = 'schema-management';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: title,
      description: description,
      url: `${SITE_URL}/management`,
      provider: {
        '@type': 'AccountingService',
        name: '横田税務会計事務所 (Yokota Tax & Accounting Office)',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '1-12-12 Higashi-Magome',
          addressLocality: 'Ota City',
          addressRegion: 'Tokyo',
          addressCountry: 'JP',
          postalCode: '143-0025',
        },
        areaServed: [
          { '@type': 'City', name: 'Ota City' },
          { '@type': 'State', name: 'Tokyo' },
        ],
        knowsAbout: [
          'Ota City tax office',
          'Tokyo accounting support',
          'Minpaku tax support Tokyo',
          'Airbnb tax consultation Tokyo',
        ],
      },
      areaServed: {
        '@type': 'State',
        name: 'Tokyo',
      },
      serviceType: t('seo.management.keywords'),
      offers: {
        '@type': 'Offer',
        description: t('schema.management.offerDescription'),
        priceCurrency: 'JPY',
        availability: 'https://schema.org/InStock',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Management Services',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: t('schema.management.service1') } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: t('schema.management.service2') } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: t('schema.management.service3') } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: t('schema.management.service4') } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: t('schema.management.service5') } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: t('schema.management.service6') } },
        ],
      },
    });

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [i18n.language, t]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection />
      <ServicesSection />
      <StrengthsSection />
      <PricingSection />
      <FlowSection />
      <TaxOfficeSection />
      <CaseStudySection />
      <FaqSection />
      <CrossLinkBanner />
      <ContactSection />
      <Footer />
    </div>
  );
}
